import type { Floor, Hotspot } from '@/types/tour';
import { currentRoomId } from '@/core/state';
import { mediaUrl } from '@/core/tour-loader';
import { hotspotDisplayLabel } from '@/core/tour-utils';

// Rendert einen Raster-Floorplan (PNG/JPG/SVG) mit darueberliegenden
// Hotspot-Punkten. Der aktive Raum wird visuell hervorgehoben.
//
// Layout-Prinzip:
//   .floorplan        position: relative, padding-top = 100/aspect%
//     <img>           absolute fill
//     .fp-hotspots    absolute fill, hotspots werden in % positioniert

export class Floorplan {
  private root: HTMLElement;
  private img: HTMLImageElement;
  private hotspotLayer: HTMLDivElement;
  private hotspotEls = new Map<string, HTMLButtonElement>();
  private unsubscribe?: () => void;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'floorplan';

    this.img = document.createElement('img');
    this.img.className = 'fp-image';
    this.img.alt = '';
    this.img.decoding = 'async';
    this.img.loading = 'eager';

    this.hotspotLayer = document.createElement('div');
    this.hotspotLayer.className = 'fp-hotspots';

    this.root.append(this.img, this.hotspotLayer);
    parent.appendChild(this.root);

    this.unsubscribe = currentRoomId.subscribe((id) => this.setActive(id));
  }

  // Tauscht das Floorplan-Bild und rendert die Hotspots der neuen Etage.
  // Vorhandene Listener werden beim Re-Render durch DOM-Replace impliziert
  // entsorgt — wir halten keinen separaten Cleanup.
  render(floor: Floor): void {
    const aspect = floor.floorplanAspect ?? 4 / 3;
    this.root.style.setProperty('--fp-aspect', String(aspect));
    this.img.src = mediaUrl(floor.floorplanSrc);
    this.img.alt = `Grundriss ${floor.name}`;

    this.hotspotLayer.innerHTML = '';
    this.hotspotEls.clear();

    floor.hotspots.forEach((hs, idx) => {
      const room = floor.rooms.find((r) => r.id === hs.roomId);
      const el = this.createHotspot(hs, hotspotDisplayLabel(hs, room, idx), floor);
      this.hotspotLayer.appendChild(el);
      this.hotspotEls.set(hs.roomId, el);
    });

    this.setActive(currentRoomId.get());
  }

  private createHotspot(
    hs: Hotspot,
    label: string,
    floor: Floor,
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fp-point';
    btn.style.left = `${hs.x}%`;
    btn.style.top = `${hs.y}%`;
    btn.dataset.roomId = hs.roomId;

    const room = floor.rooms.find((r) => r.id === hs.roomId);
    btn.setAttribute(
      'aria-label',
      room ? `Raum ${room.name} öffnen` : `Raum ${hs.roomId} öffnen`,
    );
    btn.title = room?.name ?? hs.roomId;

    const dot = document.createElement('span');
    dot.className = 'fp-point-dot';
    btn.appendChild(dot);

    if (label) {
      const lbl = document.createElement('span');
      lbl.className = 'fp-point-label';
      lbl.textContent = label;
      btn.appendChild(lbl);
    }

    btn.addEventListener('click', () => {
      currentRoomId.set(hs.roomId);
    });

    return btn;
  }

  private setActive(roomId: string): void {
    this.hotspotEls.forEach((el, id) => {
      el.classList.toggle('is-active', id === roomId);
    });
  }

  destroy(): void {
    this.unsubscribe?.();
    this.root.remove();
  }
}
